import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';

const API = `${API_V1}/inventory`;

interface Item { id: number; item_code: string; item_name: string; category: string; quantity_on_hand: number; current_cost: number | null; standard_cost: number | null; valuation_method: string; lifecycle_status: string; last_movement_date: string; }
interface Summary { total_items: number; active: number; total_value: number; below_reorder: number; }

export default function Inventory() {
  const [items, setItems] = useState<Item[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'items' | 'valuation' | 'variance' | 'obsolescence'>('items');
  const [report, setReport] = useState<any>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ item_code: '', item_name: '', category: '', standard_cost: '', current_cost: '', valuation_method: 'WEIGHTED_AVG' });

  // Phase 2 lifecycle modals (write-down / write-off / dispose / reserve).
  // Each modal carries its own form state + inline error banner (avoids
  // the z-index race where outside-modal toasts hide behind the modal).
  const [actionItem, setActionItem] = useState<Item | null>(null);
  const [actionType, setActionType] = useState<'writedown' | 'writeoff' | 'dispose' | 'reserve' | null>(null);
  const [actionForm, setActionForm] = useState<{ nrv: string; proceeds: string; disposal_date: string; reserve: string; reason: string }>({ nrv: '', proceeds: '0', disposal_date: '', reserve: '', reason: '' });
  const [modalError, setModalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Phase 4 movement modals (receive / issue / count). Receive needs
  // item + qty + unit_cost; issue needs item + qty; count needs item +
  // counted_qty. Reuses modalError + submitting state.
  const [movementType, setMovementType] = useState<'receive' | 'issue' | 'count' | null>(null);
  const [movementItem, setMovementItem] = useState<Item | null>(null);
  const [movementForm, setMovementForm] = useState({ quantity: '', unit_cost: '', counted_qty: '', notes: '' });

  // Phase 4 certify-obsolescence modal: collects reviewer reason +
  // confirmation. The actual item_ids list comes from selectedObsIds.
  const [selectedObsIds, setSelectedObsIds] = useState<Set<number>>(new Set());
  const [showCertify, setShowCertify] = useState(false);
  const [certifyReason, setCertifyReason] = useState('');

  // Errors persist until X clicked; success auto-dismisses in 4s.
  // Per Albert's 2026-05-19 feedback: actionable failures shouldn't vanish.
  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    if (ok) setTimeout(() => setToast(null), 4000);
  };

  const openAction = (type: 'writedown' | 'writeoff' | 'dispose' | 'reserve', item: Item) => {
    setActionItem(item);
    setActionType(type);
    setActionForm({
      nrv: item.current_cost != null ? String(item.current_cost) : '',
      proceeds: '0',
      disposal_date: new Date().toISOString().slice(0, 10),
      reserve: '',
      reason: '',
    });
    setModalError(null);
  };
  const closeAction = () => { setActionItem(null); setActionType(null); setModalError(null); };

  const submitAction = async () => {
    if (!actionItem || !actionType) return;
    setModalError(null);
    setSubmitting(true);
    try {
      let url = '';
      let payload: any = { reason: actionForm.reason };
      if (actionType === 'writedown') {
        url = `${API}/items/${actionItem.id}/writedown`;
        const nrv = parseFloat(actionForm.nrv);
        if (Number.isNaN(nrv)) { setModalError('New NRV per unit must be a number'); setSubmitting(false); return; }
        payload.new_nrv_per_unit = nrv;
      } else if (actionType === 'writeoff') {
        url = `${API}/items/${actionItem.id}/writeoff`;
      } else if (actionType === 'dispose') {
        url = `${API}/items/${actionItem.id}/dispose`;
        const proceeds = parseFloat(actionForm.proceeds || '0');
        if (Number.isNaN(proceeds) || proceeds < 0) { setModalError('Proceeds must be a non-negative number'); setSubmitting(false); return; }
        payload.proceeds = proceeds;
        if (actionForm.disposal_date) payload.disposal_date = actionForm.disposal_date;
      } else if (actionType === 'reserve') {
        url = `${API}/items/${actionItem.id}/reserve`;
        const r = parseFloat(actionForm.reserve);
        if (Number.isNaN(r) || r < 0) { setModalError('Reserve amount must be a non-negative number'); setSubmitting(false); return; }
        payload.reserve_amount = r;
      }
      const res = await authFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) {
        const d = await res.json();
        const summary = (
          actionType === 'writedown' ? `Wrote down ${actionItem.item_code} by ${formatAmount(d.writedown_amount || 0)}` :
          actionType === 'writeoff' ? `Wrote off ${actionItem.item_code} (${formatAmount(d.writeoff_amount || 0)})` :
          actionType === 'dispose' ? `Disposed ${actionItem.item_code} -- ${d.gain_loss >= 0 ? 'gain' : 'loss'} ${formatAmount(Math.abs(d.gain_loss || 0))}` :
          `Reserve updated for ${actionItem.item_code} (delta ${formatAmount(d.delta || 0)})`
        );
        showToast(summary, true);
        closeAction();
        load();
      } else {
        const e = await res.json().catch(() => ({}));
        const msg = e.detail || `HTTP ${res.status}`;
        setModalError(msg);
        showToast(msg, false);
      }
    } catch (err: any) {
      const msg = err?.message || 'Network error';
      setModalError(msg);
      showToast(msg, false);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Phase 4 movement handlers (receive / issue / count) ──
  const openMovement = (type: 'receive' | 'issue' | 'count', item?: Item) => {
    setMovementType(type);
    setMovementItem(item || null);
    setMovementForm({
      quantity: '',
      unit_cost: item?.current_cost != null ? String(item.current_cost) : '',
      counted_qty: item?.quantity_on_hand != null ? String(item.quantity_on_hand) : '',
      notes: '',
    });
    setModalError(null);
  };
  const closeMovement = () => { setMovementType(null); setMovementItem(null); setModalError(null); };

  const submitMovement = async () => {
    if (!movementType || !movementItem) {
      setModalError('Pick an item first');
      return;
    }
    setModalError(null);
    setSubmitting(true);
    try {
      let url = '';
      let payload: any = { item_id: movementItem.id };
      if (movementType === 'receive') {
        const qty = parseFloat(movementForm.quantity);
        const cost = parseFloat(movementForm.unit_cost);
        if (Number.isNaN(qty) || qty <= 0) { setModalError('Quantity must be > 0'); setSubmitting(false); return; }
        if (Number.isNaN(cost) || cost <= 0) { setModalError('Unit cost must be > 0'); setSubmitting(false); return; }
        url = `${API}/receipt`;
        payload.quantity = qty;
        payload.unit_cost = cost;
        payload.reference_type = 'MANUAL';
      } else if (movementType === 'issue') {
        const qty = parseFloat(movementForm.quantity);
        if (Number.isNaN(qty) || qty <= 0) { setModalError('Quantity must be > 0'); setSubmitting(false); return; }
        url = `${API}/issue`;
        payload.quantity = qty;
        payload.reference_type = 'MANUAL';
      } else if (movementType === 'count') {
        const cnt = parseFloat(movementForm.counted_qty);
        if (Number.isNaN(cnt) || cnt < 0) { setModalError('Counted qty must be a non-negative number'); setSubmitting(false); return; }
        url = `${API}/cycle-count`;
        payload.counted_qty = cnt;
      }
      const res = await authFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) {
        const d = await res.json();
        const summary = (
          movementType === 'receive' ? `Received ${movementForm.quantity} of ${movementItem.item_code} @ $${movementForm.unit_cost}` :
          movementType === 'issue' ? `Issued ${movementForm.quantity} of ${movementItem.item_code}` :
          d.adjustment_status === 'AUTO_ADJUSTED'
            ? `Count recorded for ${movementItem.item_code} -- auto-adjusted (variance within 2%)`
            : `Count recorded for ${movementItem.item_code} -- variance ${d.variance_pct}% pending approval`
        );
        showToast(summary, true);
        closeMovement();
        load();
      } else {
        const e = await res.json().catch(() => ({}));
        const msg = e.detail || `HTTP ${res.status}`;
        setModalError(msg);
        showToast(msg, false);
      }
    } catch (err: any) {
      const msg = err?.message || 'Network error';
      setModalError(msg);
      showToast(msg, false);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Phase 4 certify-obsolescence handler ──
  const toggleObs = (id: number) => {
    setSelectedObsIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submitCertify = async () => {
    if (selectedObsIds.size === 0) { setModalError('Select at least one item'); return; }
    if (certifyReason.trim().length < 5) { setModalError('Reason must be at least 5 characters'); return; }
    setModalError(null);
    setSubmitting(true);
    try {
      const res = await authFetch(`${API}/obsolescence/certify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_ids: Array.from(selectedObsIds), reason: certifyReason }),
      });
      if (res.ok) {
        const d = await res.json();
        showToast(`Certified ${d.certified_count} reserve JE(s)${d.error_count ? `, ${d.error_count} failed` : ''}`, d.error_count === 0);
        setShowCertify(false);
        setSelectedObsIds(new Set());
        setCertifyReason('');
        loadReport('obsolescence');
      } else {
        const e = await res.json().catch(() => ({}));
        const msg = e.detail || `HTTP ${res.status}`;
        setModalError(msg);
        showToast(msg, false);
      }
    } catch (err: any) {
      const msg = err?.message || 'Network error';
      setModalError(msg);
      showToast(msg, false);
    } finally {
      setSubmitting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, sRes] = await Promise.all([authFetch(`${API}/items?limit=100`), authFetch(`${API}/summary`)]);
      if (iRes.ok) { const d = await iRes.json(); setItems(d.items || []); }
      if (sRes.ok) setSummary(await sRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadReport = async (type: string) => {
    setTab(type as any);
    const res = await authFetch(`${API}/${type}`);
    if (res.ok) setReport(await res.json());
  };

  const handleCreate = async () => {
    try {
      const payload = { ...form, standard_cost: form.standard_cost ? parseFloat(form.standard_cost) : null, current_cost: form.current_cost ? parseFloat(form.current_cost) : null };
      const res = await authFetch(`${API}/items`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) { showToast('Item created', true); setShowCreate(false); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const handleTabChange = (key: string) => {
    if (key === 'items') setTab('items');
    else loadReport(key);
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const statusColor = (s: string) =>
    s === 'WRITTEN_OFF' || s === 'DISPOSED' ? 'var(--negative)' :
    s === 'SLOW_MOVING' || s === 'OBSOLETE' ? 'var(--warning)' :
    'var(--positive)';

  const itemColumns: Column[] = [
    { key: 'item_code', label: 'Code', render: (v: any) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{v}</span> },
    { key: 'item_name', label: 'Name', render: (v: any) => v || '-' },
    { key: 'category', label: 'Category', render: (v: any) => v || '-' },
    { key: 'quantity_on_hand', label: 'Qty', align: 'right', render: (v: any) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'current_cost', label: 'Cost', align: 'right', render: (v: any) => v != null ? `$${v.toFixed(2)}` : '-' },
    { key: 'valuation_method', label: 'Method' },
    { key: 'lifecycle_status', label: 'Status', render: (v: any) => <span style={{ fontSize: 10, fontWeight: 700, color: statusColor(v), background: 'transparent', border: `1px solid ${statusColor(v)}`, padding: '2px 6px', borderRadius: 4 }}>{v || 'ACTIVE'}</span> },
    { key: 'last_movement_date', label: 'Last Move', render: (v: any) => v || '-' },
    {
      key: 'actions', label: 'Actions',
      render: (_v: any, row: any) => {
        const closed = row.lifecycle_status === 'WRITTEN_OFF' || row.lifecycle_status === 'DISPOSED';
        if (closed) return <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>closed</span>;
        const btn: React.CSSProperties = { padding: '3px 8px', fontSize: 10, fontWeight: 600, borderRadius: 4, border: '1px solid var(--border-default)', background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' };
        const hasQty = (row.quantity_on_hand || 0) > 0;
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" style={{ ...btn, color: 'var(--accent)', borderColor: 'var(--accent)' }} title="Record goods receipt (manual)" onClick={() => openMovement('receive', row)}>Receive</button>
            {hasQty && <button type="button" style={{ ...btn, color: 'var(--accent)', borderColor: 'var(--accent)' }} title="Issue stock (DR COGS / CR Inventory)" onClick={() => openMovement('issue', row)}>Issue</button>}
            <button type="button" style={btn} title="Record a physical count" onClick={() => openMovement('count', row)}>Count</button>
            <button type="button" style={btn} title="Reduce carrying value to NRV (LCM)" onClick={() => openAction('writedown', row)}>Write-down</button>
            <button type="button" style={{ ...btn, color: 'var(--warning)', borderColor: 'var(--warning)' }} title="Establish obsolescence reserve" onClick={() => openAction('reserve', row)}>Reserve</button>
            <button type="button" style={{ ...btn, color: 'var(--negative)', borderColor: 'var(--negative)' }} title="Sell / scrap / donate" onClick={() => openAction('dispose', row)}>Dispose</button>
            <button type="button" style={{ ...btn, color: 'var(--negative)', borderColor: 'var(--negative)' }} title="Remove full carrying to write-down expense" onClick={() => openAction('writeoff', row)}>Write-off</button>
          </div>
        );
      },
    },
  ];

  const valuationColumns: Column[] = [
    { key: 'item_code', label: 'Item', render: (v: any) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'quantity', label: 'Qty', align: 'right' },
    { key: 'unit_cost', label: 'Cost', align: 'right', render: (v: any) => `$${v?.toFixed(2)}` },
    { key: 'carrying_value', label: 'Carrying', align: 'right', render: (v: any, row: any) => <span style={{ fontWeight: 600 }}>{formatAmount(row.carrying_value)}</span> },
    { key: 'nrv', label: 'NRV', align: 'right', render: (v: any) => v != null ? formatAmount(v) : '-' },
    { key: 'writedown', label: 'Write-Down', align: 'right', render: (v: any) => <span style={{ color: v > 0 ? 'var(--negative)' : 'var(--text-secondary)' }}>{formatAmount(v)}</span> },
  ];

  const varianceColumns: Column[] = [
    { key: 'item_code', label: 'Item', render: (v: any) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'standard_cost', label: 'Standard', align: 'right', render: (v: any) => `$${v?.toFixed(2)}` },
    { key: 'actual_cost', label: 'Actual', align: 'right', render: (v: any) => `$${v?.toFixed(2)}` },
    { key: 'variance_amount', label: 'Variance $', align: 'right', render: (v: any, row: any) => <span style={{ fontWeight: 600, color: row.favorable ? 'var(--positive)' : 'var(--negative)' }}>{formatAmount(v)}</span> },
    { key: 'variance_pct', label: 'Variance %', align: 'right', render: (v: any) => `${v}%` },
    { key: 'favorable', label: 'F/U', render: (v: any) => <span style={{ fontWeight: 600, color: v ? 'var(--positive)' : 'var(--negative)' }}>{v ? 'FAV' : 'UNFAV'}</span> },
  ];

  return (
    <PageLayout title="Inventory" subtitle="Item master, valuation, variance analysis, and obsolescence" action={{ label: '+ New Item', onClick: () => setShowCreate(true) }}>
      {summary && (
        <KpiGrid items={[
          { label: 'Items', value: summary.total_items },
          { label: 'Active', value: summary.active, color: 'var(--positive)' },
          { label: 'Total Value', value: formatAmountCompact(summary.total_value), color: 'var(--accent)' },
          { label: 'Below Reorder', value: summary.below_reorder, color: summary.below_reorder > 0 ? 'var(--negative)' : 'var(--positive)' },
        ]} columns={4} />
      )}

      <TabBar
        tabs={[
          { key: 'items', label: 'Items' },
          { key: 'valuation', label: 'Valuation' },
          { key: 'variance', label: 'Variance' },
          { key: 'obsolescence', label: 'Obsolescence' },
        ]}
        active={tab}
        onChange={handleTabChange}
      />

      {tab === 'items' ? (
        <DataTable columns={itemColumns} rows={items} loading={loading} emptyMessage="No inventory items." rowKey="id" />
      ) : tab === 'valuation' && report ? (
        <div>
          <KpiGrid items={[
            { label: 'Total Value', value: formatAmountCompact(report.total_carrying_value), color: 'var(--accent)' },
            { label: 'NRV Write-Down', value: formatAmountCompact(report.total_nrv_writedown), color: 'var(--negative)' },
          ]} columns={2} />
          {report.items?.length > 0 && <DataTable columns={valuationColumns} rows={report.items} emptyMessage="No valuation data." />}
        </div>
      ) : tab === 'variance' && report ? (
        <div>
          <KpiGrid items={[
            { label: 'Total Variance', value: formatAmountCompact(report.total_variance), color: report.total_variance >= 0 ? 'var(--negative)' : 'var(--positive)' },
          ]} columns={1} />
          {report.variances?.length > 0 && <DataTable columns={varianceColumns} rows={report.variances} emptyMessage="No variance data." />}
        </div>
      ) : tab === 'obsolescence' && report ? (
        <div>
          <KpiGrid items={[
            { label: 'Recommended Reserve', value: formatAmountCompact(report.total_reserve), color: 'var(--negative)' },
            { label: 'Selected', value: selectedObsIds.size, color: 'var(--accent)' },
          ]} columns={2} />
          <div style={{ display: 'flex', gap: 8, margin: '8px 0 12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <ActionButton
              label={`Certify Selected (${selectedObsIds.size})`}
              onClick={() => { setCertifyReason(''); setModalError(null); setShowCertify(true); }}
              variant="primary"
              disabled={selectedObsIds.size === 0}
            />
            <button type="button" onClick={() => {
              const all = [...(report.slow_moving || []), ...(report.dead_stock || [])].map((r: any) => r.item_id);
              setSelectedObsIds(new Set(all));
            }} style={{ padding: '6px 10px', fontSize: 11, border: '1px solid var(--border-default)', borderRadius: 4, background: 'var(--bg-card)', cursor: 'pointer' }}>Select all</button>
            <button type="button" onClick={() => setSelectedObsIds(new Set())} style={{ padding: '6px 10px', fontSize: 11, border: '1px solid var(--border-default)', borderRadius: 4, background: 'var(--bg-card)', cursor: 'pointer' }}>Clear</button>
            {report.thresholds && (
              <span style={{ fontSize: 10, color: 'var(--text-tertiary)', marginLeft: 'auto' }}>
                Thresholds: slow {report.thresholds.slow_days}d / {Math.round((report.thresholds.slow_pct || 0) * 100)}% &middot; dead {report.thresholds.dead_days}d / {Math.round((report.thresholds.dead_pct || 0) * 100)}%
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'var(--warning)' }}>Slow Moving ({report.slow_count})</div>
              {report.slow_moving?.map((s: any) => (
                <label key={s.item_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedObsIds.has(s.item_id)} onChange={() => toggleObs(s.item_id)} aria-label={`Select ${s.item_code}`} />
                  <span style={{ flex: 1 }}>{s.item_code} ({s.days_since_movement}d)</span>
                  <span style={{ color: 'var(--warning)' }}>{formatAmount(s.reserve_amount)} ({s.reserve_pct}%)</span>
                </label>
              ))}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'var(--negative)' }}>Dead Stock ({report.dead_count})</div>
              {report.dead_stock?.map((d: any) => (
                <label key={d.item_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedObsIds.has(d.item_id)} onChange={() => toggleObs(d.item_id)} aria-label={`Select ${d.item_code}`} />
                  <span style={{ flex: 1 }}>{d.item_code} ({d.days_since_movement}d)</span>
                  <span style={{ color: 'var(--negative)' }}>{formatAmount(d.reserve_amount)} ({d.reserve_pct}%)</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Inventory Item" footer={<><ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" /><ActionButton label="Create" onClick={handleCreate} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}><div style={{ display: 'flex', gap: 8 }}><input placeholder="Item Code *" value={form.item_code} onChange={e => setForm({ ...form, item_code: e.target.value })} style={inputStyle} /><input placeholder="Item Name" value={form.item_name} onChange={e => setForm({ ...form, item_name: e.target.value })} style={inputStyle} /></div><div style={{ display: 'flex', gap: 8 }}><input placeholder="Category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={inputStyle} /><select value={form.valuation_method} onChange={e => setForm({ ...form, valuation_method: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}><option value="WEIGHTED_AVG">Weighted Average</option><option value="FIFO">FIFO</option><option value="STANDARD">Standard Cost</option></select></div><div style={{ display: 'flex', gap: 8 }}><input placeholder="Standard Cost" type="number" value={form.standard_cost} onChange={e => setForm({ ...form, standard_cost: e.target.value })} style={inputStyle} /><input placeholder="Current Cost" type="number" value={form.current_cost} onChange={e => setForm({ ...form, current_cost: e.target.value })} style={inputStyle} /></div></div>
      </Modal>

      {/* Phase 2 lifecycle modal -- one modal handles all 4 actions
          since their form shapes mostly overlap (reason + a couple
          of action-specific fields). Inline error banner pattern
          matches APInvoices upload modal (z-index race fix). */}
      <Modal
        open={actionItem != null && actionType != null}
        onClose={closeAction}
        title={
          actionType === 'writedown' ? `Write down ${actionItem?.item_code} to NRV` :
          actionType === 'writeoff' ? `Write off ${actionItem?.item_code}` :
          actionType === 'dispose' ? `Dispose ${actionItem?.item_code}` :
          actionType === 'reserve' ? `Obsolescence reserve: ${actionItem?.item_code}` :
          ''
        }
        footer={
          <>
            <ActionButton label="Cancel" onClick={closeAction} variant="secondary" />
            <ActionButton
              label={submitting ? 'Posting...' : (actionType === 'writeoff' || actionType === 'dispose' ? 'Confirm' : 'Post JE')}
              onClick={submitAction}
              variant={actionType === 'writeoff' || actionType === 'dispose' ? 'danger' : 'primary'}
              disabled={submitting || !actionForm.reason || actionForm.reason.trim().length < 5}
            />
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {modalError && (
            <div style={{
              padding: '12px 16px', borderRadius: 8,
              background: 'var(--negative-bg)', color: 'var(--negative)',
              border: '1px solid var(--negative)',
              fontSize: 13, lineHeight: 1.5,
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  {actionType === 'writedown' ? 'Write-down failed' :
                   actionType === 'writeoff' ? 'Write-off failed' :
                   actionType === 'dispose' ? 'Dispose failed' : 'Reserve failed'}
                </div>
                {modalError}
              </div>
              <button type="button" onClick={() => setModalError(null)} aria-label="Dismiss error"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 18, fontWeight: 700, lineHeight: 1, padding: 0, marginTop: -2 }}>×</button>
            </div>
          )}

          {actionItem && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', padding: '8px 10px', background: 'var(--bg-subtle, #f5f5f5)', borderRadius: 6 }}>
              Qty on hand: <strong>{actionItem.quantity_on_hand}</strong> &middot;
              {' '}Current cost: <strong>${actionItem.current_cost?.toFixed(2)}</strong> &middot;
              {' '}Carrying: <strong>{formatAmount((actionItem.quantity_on_hand || 0) * (actionItem.current_cost || 0))}</strong>
            </div>
          )}

          {actionType === 'writedown' && (
            <>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>NEW NRV (per unit, must be below current cost)</label>
              <input placeholder="New NRV per unit *" type="number" step="0.01" value={actionForm.nrv}
                onChange={e => setActionForm({ ...actionForm, nrv: e.target.value })} style={inputStyle} />
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                Will post DR Inventory Write-down / CR Inventory for qty * (current_cost - new_nrv).
                Item moves to SLOW_MOVING.
              </div>
            </>
          )}

          {actionType === 'writeoff' && (
            <div style={{ fontSize: 11, color: 'var(--negative)', padding: '8px 10px', background: 'var(--negative-bg, #fee)', borderRadius: 6, border: '1px solid var(--negative)' }}>
              Will remove the full carrying value via DR Inventory Write-down / CR Inventory.
              Item moves to <strong>WRITTEN_OFF</strong> and qty_on_hand becomes 0.
              This cannot be undone -- only use for unsellable / lost / scrapped goods with no recovery.
            </div>
          )}

          {actionType === 'dispose' && (
            <>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>PROCEEDS (cash received, 0 if scrapped/donated)</label>
              <input placeholder="Proceeds" type="number" step="0.01" value={actionForm.proceeds}
                onChange={e => setActionForm({ ...actionForm, proceeds: e.target.value })} style={inputStyle} />
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>DISPOSAL DATE</label>
              <input type="date" aria-label="Disposal date" title="Disposal date" value={actionForm.disposal_date}
                onChange={e => setActionForm({ ...actionForm, disposal_date: e.target.value })} style={inputStyle} />
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                Will post DR Cash (proceeds) + DR/CR Gain/Loss on Disposal + CR Inventory (full carrying).
                Item moves to <strong>DISPOSED</strong>.
              </div>
            </>
          )}

          {actionType === 'reserve' && (
            <>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>RESERVE AMOUNT (total $, not per unit)</label>
              <input placeholder="Reserve amount" type="number" step="0.01" value={actionForm.reserve}
                onChange={e => setActionForm({ ...actionForm, reserve: e.target.value })} style={inputStyle} />
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                Will post DR Inventory Write-down / CR Inventory Reserve (contra-asset).
                Reserve sits next to Inventory on the BS; net presentation = NRV.
                Asset stays on the book; carrying value untouched.
              </div>
            </>
          )}

          <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>REASON (min 5 chars, SOX 404 audit trail)</label>
          <textarea placeholder="Why is this happening? (e.g. 'NRV test Q2-26 -- product overstocked, sales velocity dropped')"
            value={actionForm.reason}
            onChange={e => setActionForm({ ...actionForm, reason: e.target.value })}
            style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit', resize: 'vertical' }} />
        </div>
      </Modal>

      {/* Phase 4 movement modal -- one modal handles receive / issue /
          count. Same inline error banner + persistent toast pattern
          as the Phase 2 lifecycle modal. */}
      <Modal
        open={movementType != null}
        onClose={closeMovement}
        title={
          movementType === 'receive' ? `Receive goods: ${movementItem?.item_code}` :
          movementType === 'issue' ? `Issue goods: ${movementItem?.item_code}` :
          movementType === 'count' ? `Cycle count: ${movementItem?.item_code}` :
          ''
        }
        footer={
          <>
            <ActionButton label="Cancel" onClick={closeMovement} variant="secondary" />
            <ActionButton
              label={submitting ? 'Posting...' : (movementType === 'receive' ? 'Receive & Post' : movementType === 'issue' ? 'Issue & Post' : 'Record Count')}
              onClick={submitMovement}
              variant="primary"
              disabled={submitting}
            />
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {modalError && (
            <div style={{
              padding: '12px 16px', borderRadius: 8,
              background: 'var(--negative-bg)', color: 'var(--negative)',
              border: '1px solid var(--negative)',
              fontSize: 13, lineHeight: 1.5,
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                  {movementType === 'receive' ? 'Receive failed' :
                   movementType === 'issue' ? 'Issue failed' :
                   'Count failed'}
                </div>
                {modalError}
              </div>
              <button type="button" onClick={() => setModalError(null)} aria-label="Dismiss error"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 18, fontWeight: 700, lineHeight: 1, padding: 0, marginTop: -2 }}>×</button>
            </div>
          )}

          {movementItem && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', padding: '8px 10px', background: 'var(--bg-subtle, #f5f5f5)', borderRadius: 6 }}>
              Qty on hand: <strong>{movementItem.quantity_on_hand}</strong> &middot;
              {' '}Current cost: <strong>${movementItem.current_cost?.toFixed(2)}</strong> &middot;
              {' '}Method: <strong>{movementItem.valuation_method}</strong>
            </div>
          )}

          {movementType === 'receive' && (
            <>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>QUANTITY RECEIVED</label>
              <input placeholder="Quantity *" type="number" step="0.01" value={movementForm.quantity}
                onChange={e => setMovementForm({ ...movementForm, quantity: e.target.value })} style={inputStyle} />
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>UNIT COST</label>
              <input placeholder="Unit cost *" type="number" step="0.0001" value={movementForm.unit_cost}
                onChange={e => setMovementForm({ ...movementForm, unit_cost: e.target.value })} style={inputStyle} />
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                Will post DR Inventory / CR GRNI. For WAvg items, current_cost blends with the receipt cost.
                For FIFO, a new cost layer is created.
                <br />
                Use the GR module (P2P flow) for receipts against a PO -- this manual entry path is for adhoc receipts.
              </div>
            </>
          )}

          {movementType === 'issue' && (
            <>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>QUANTITY ISSUED</label>
              <input placeholder="Quantity *" type="number" step="0.01" value={movementForm.quantity}
                onChange={e => setMovementForm({ ...movementForm, quantity: e.target.value })} style={inputStyle} />
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                Will post DR COGS / CR Inventory at the item's current cost (WAvg) or by consuming oldest layers first (FIFO).
                Requires qty_on_hand &gt;= requested quantity.
              </div>
            </>
          )}

          {movementType === 'count' && (
            <>
              <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>PHYSICAL COUNT (system qty: {movementItem?.quantity_on_hand})</label>
              <input placeholder="Counted quantity *" type="number" step="0.01" value={movementForm.counted_qty}
                onChange={e => setMovementForm({ ...movementForm, counted_qty: e.target.value })} style={inputStyle} />
              <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>
                Variance within 2% of system qty auto-adjusts on submit.
                Variance &gt; 2% records the count as PENDING for reviewer approval (no qty change yet).
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Phase 4 certify-obsolescence modal -- batch reserve JE for
          selected slow-moving / dead-stock recommendations. */}
      <Modal
        open={showCertify}
        onClose={() => { setShowCertify(false); setModalError(null); }}
        title={`Certify ${selectedObsIds.size} obsolescence reserve(s)`}
        footer={
          <>
            <ActionButton label="Cancel" onClick={() => { setShowCertify(false); setModalError(null); }} variant="secondary" />
            <ActionButton
              label={submitting ? 'Posting JEs...' : 'Certify & Post'}
              onClick={submitCertify}
              variant="primary"
              disabled={submitting || selectedObsIds.size === 0 || certifyReason.trim().length < 5}
            />
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {modalError && (
            <div style={{
              padding: '12px 16px', borderRadius: 8,
              background: 'var(--negative-bg)', color: 'var(--negative)',
              border: '1px solid var(--negative)',
              fontSize: 13, lineHeight: 1.5,
              display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Certify failed</div>
                {modalError}
              </div>
              <button type="button" onClick={() => setModalError(null)} aria-label="Dismiss error"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 18, fontWeight: 700, lineHeight: 1, padding: 0, marginTop: -2 }}>×</button>
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            One reserve JE will post per selected item at the recommended reserve %
            ({(report?.thresholds?.slow_pct ? Math.round(report.thresholds.slow_pct * 100) : 30)}% slow / {(report?.thresholds?.dead_pct ? Math.round(report.thresholds.dead_pct * 100) : 80)}% dead).
            Each JE: DR Inventory Write-down / CR Inventory Reserve (contra-asset).
            Idempotent -- re-certifying with the same amount posts delta=0.
          </div>
          <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>REASON (min 5 chars, SOX 404 audit trail)</label>
          <textarea placeholder="Why is this batch being certified now? (e.g. 'Q2-26 quarter-end NRV review, finance sign-off attached')"
            value={certifyReason}
            onChange={e => setCertifyReason(e.target.value)}
            style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit', resize: 'vertical' }} />
        </div>
      </Modal>

      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 2000,
          padding: '12px 16px 12px 20px', borderRadius: 8,
          background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)',
          color: toast.ok ? 'var(--positive)' : 'var(--negative)',
          fontSize: 13, fontWeight: 600,
          maxWidth: 520, lineHeight: 1.4,
          display: 'flex', alignItems: 'flex-start', gap: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{toast.msg}</span>
          {!toast.ok && (
            <button type="button" onClick={() => setToast(null)} aria-label="Dismiss"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 18, fontWeight: 700, lineHeight: 1, padding: 0, marginTop: -2 }}>×</button>
          )}
        </div>
      )}
    </PageLayout>
  );
}
